/**
 * VerseView Component
 *
 * Displays a single Torah verse with:
 * - Hebrew text (RTL, serif font)
 * - English translation
 * - Multiple layout modes (stacked, side-by-side, single language)
 * - Highlight toggle
 * - Commentary expand button
 * - Favorite button integration
 */

import { memo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { FavoriteButton } from '@/components/FavoriteButton';
import type { VerseViewProps, ReaderLayout, ReaderFontSize } from '@/types/sefaria';

// ============================================================
// Font Size Classes
// ============================================================

const FONT_SIZE_CLASSES: Record<ReaderFontSize, { hebrew: string; english: string }> = {
  small: {
    hebrew: 'text-lg leading-relaxed',
    english: 'text-sm leading-relaxed',
  },
  medium: {
    hebrew: 'text-xl leading-relaxed',
    english: 'text-base leading-relaxed',
  },
  large: {
    hebrew: 'text-2xl leading-relaxed',
    english: 'text-lg leading-relaxed',
  },
  xlarge: {
    hebrew: 'text-3xl leading-loose',
    english: 'text-xl leading-relaxed',
  },
};

// ============================================================
// Layout Renderers
// ============================================================

interface TextContentProps {
  verse: VerseViewProps['verse'];
  fontSize: ReaderFontSize;
  layout: ReaderLayout;
}

function HebrewText({ verse, fontSize }: TextContentProps) {
  const sizeClasses = FONT_SIZE_CLASSES[fontSize];

  return (
    <p
      className={`font-serif ${sizeClasses.hebrew} text-slate-800`}
      dir="rtl"
      lang="he"
      style={{ fontFamily: "'Frank Ruhl Libre', 'Ezra SIL', serif" }}
    >
      {verse.hebrew}
    </p>
  );
}

function EnglishText({ verse, fontSize }: TextContentProps) {
  const sizeClasses = FONT_SIZE_CLASSES[fontSize];

  return <p className={`${sizeClasses.english} text-slate-600`}>{verse.english}</p>;
}

// ============================================================
// Main Component
// ============================================================

function VerseViewComponent({
  verse,
  layout,
  fontSize,
  isHighlighted,
  isCommentaryExpanded,
  isBlinking,
  onHighlight,
  onToggleCommentary,
  userId,
  parshaContext,
  parshaRef,
  parshaHebrew,
}: VerseViewProps) {
  const handleVerseClick = () => {
    onHighlight(verse.ref);
  };

  const handleCommentaryToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleCommentary(verse.ref);
  };

  // Render content based on layout mode
  const renderContent = () => {
    switch (layout) {
      case 'hebrew-only':
        return (
          <div className="space-y-2">
            <HebrewText verse={verse} fontSize={fontSize} layout={layout} />
          </div>
        );

      case 'english-only':
        return (
          <div className="space-y-2">
            <EnglishText verse={verse} fontSize={fontSize} layout={layout} />
          </div>
        );

      case 'side-by-side':
        return (
          <div className="grid grid-cols-2 gap-4">
            {/* Hebrew on the right (RTL reading) */}
            <div className="order-2">
              <HebrewText verse={verse} fontSize={fontSize} layout={layout} />
            </div>
            {/* English on the left */}
            <div className="order-1">
              <EnglishText verse={verse} fontSize={fontSize} layout={layout} />
            </div>
          </div>
        );

      case 'stacked':
      default:
        return (
          <div className="space-y-3">
            <HebrewText verse={verse} fontSize={fontSize} layout={layout} />
            <EnglishText verse={verse} fontSize={fontSize} layout={layout} />
          </div>
        );
    }
  };

  // Build the className for the article
  const getArticleClassName = () => {
    const baseClasses = 'relative p-4 rounded-lg transition-colors cursor-pointer';

    if (isBlinking) {
      // Gentle purple glow animation for focused verse from favorites
      return `${baseClasses} bg-purple-100 border-l-4 border-[#8e4585] ring-2 ring-[#8e4585]/30 animate-pulse`;
    }

    if (isHighlighted) {
      return `${baseClasses} bg-purple-50 border-l-4 border-[#8e4585]`;
    }

    return `${baseClasses} bg-white hover:bg-slate-50`;
  };

  return (
    <article
      className={getArticleClassName()}
      onClick={handleVerseClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleVerseClick();
        }
      }}
      aria-pressed={isHighlighted}
      aria-label={`Verse ${verse.verseNumber}${isHighlighted ? ', highlighted' : ''}`}
    >
      {/* Verse number indicator */}
      <div className="absolute left-0 top-0 bottom-0 flex items-start pt-4">
        <span
          className={`
            w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold
            ${isHighlighted ? 'bg-[#8e4585] text-white' : 'bg-slate-100 text-slate-500'}
          `}
        >
          {verse.verseNumber}
        </span>
      </div>

      {/* Main content with left padding for verse number */}
      <div className="ml-10">
        {renderContent()}

        {/* Actions row */}
        <div className="mt-3 flex items-center justify-between">
          {/* Commentary toggle */}
          {verse.hasCommentary && (
            <button
              onClick={handleCommentaryToggle}
              className={`
                flex items-center gap-1 text-sm font-medium transition-colors
                ${isCommentaryExpanded ? 'text-[#8e4585]' : 'text-slate-400 hover:text-slate-600'}
              `}
              aria-expanded={isCommentaryExpanded}
              aria-label={isCommentaryExpanded ? 'Collapse commentary' : 'Expand commentary'}
            >
              {isCommentaryExpanded ? (
                <>
                  <ChevronUp size={16} />
                  <span>Hide Commentary</span>
                </>
              ) : (
                <>
                  <ChevronDown size={16} />
                  <span>Commentary</span>
                </>
              )}
            </button>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Highlight indicator */}
            {isHighlighted && (
              <span className="text-xs text-[#8e4585] font-medium">Highlighted</span>
            )}

            {/* Favorite button */}
            {userId && (
              <div onClick={(e) => e.stopPropagation()}>
                <FavoriteButton
                  itemId={verse.ref}
                  itemType="verse"
                  cachedData={{
                    title: verse.ref,
                    preview:
                      verse.english.slice(0, 100) + (verse.english.length > 100 ? '...' : ''),
                    hebrewText: verse.hebrew,
                    parshaContext: parshaContext,
                    parshaRef: parshaRef,
                    parshaHebrew: parshaHebrew,
                  }}
                  size="sm"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

// Memoize to prevent unnecessary re-renders
export const VerseView = memo(VerseViewComponent);
